import { z } from 'zod';
import { uuid, isoTimestamp, syncColumns } from '../schemas/common.js';
import { WORDLE_SYNC_TABLES, GAME_STATUSES } from './enums.js';
/**
 * Row and protocol shapes for the Wordle module.
 *
 * Like the todo and agenda schemas, `household_id` is deliberately absent from
 * the row — it is derived from the session server-side — and the module declares
 * its own mutation union and pull response over its one table.
 *
 * **Only the answer and the guesses are stored.** The tile grid (evaluations) is
 * recomputed from `target_word` + `guesses` by `restoreGame`, so it can never
 * drift from the guesses; `status` is stored for cheap leaderboard/streak reads.
 */
export const wordleSyncTable = z.enum(WORDLE_SYNC_TABLES);
export const gameStatus = z.enum(GAME_STATUSES);
/** A calendar date `YYYY-MM-DD` — the shared daily puzzle's identity. */
export const puzzleDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
/* ---------------- row ---------------- */
export const wordleGameRow = syncColumns.extend({
    id: uuid,
    /** Which day's puzzle this game is. One game per player per date. */
    puzzle_date: puzzleDate,
    /** The answer, stored so history and the end-of-game reveal are self-contained. */
    target_word: z.string().min(1).max(15),
    /** Submitted guesses, normalized uppercase, oldest first. */
    guesses: z.array(z.string().max(15)).max(12).default([]),
    status: gameStatus.default('in_progress'),
    /** The player. A game is always one member's own board. */
    created_by: uuid.nullish(),
});
/* ---------------- mutations ---------------- */
const mutationId = z.object({ id: uuid });
const wordleUpsert = mutationId.extend({
    op: z.literal('upsert'),
    table: z.literal('wordle_games'),
    row: wordleGameRow,
});
/** A soft delete. Rows are never hard-deleted; the tombstone propagates. */
const wordleDelete = mutationId.extend({
    op: z.literal('delete'),
    table: wordleSyncTable,
    row: z.object({ id: uuid, updated_at: isoTimestamp }),
});
export const wordleMutation = z.union([wordleUpsert, wordleDelete]);
export const wordlePushRequest = z.object({
    mutations: z.array(wordleMutation).max(500),
});
export const wordlePushResponse = z.object({
    applied: z.array(uuid),
    rejected: z.array(z.object({ id: uuid, code: z.string(), message: z.string() })),
    server_time: isoTimestamp,
});
export const wordlePullResponse = z.object({
    server_time: isoTimestamp,
    changes: z.object({
        wordle_games: z.array(z.record(z.unknown())),
    }),
    has_more: z.boolean(),
    cursor: z.string().optional(),
    full_resync: z.boolean().optional(),
});
/* ---------------- read: recent games ---------------- */
/**
 * `GET /api/v1/wordle`. The household's recent games (both members), newest day
 * first — backs first paint, the contract test, and the partner/leaderboard
 * panel. The client normally reads this from its synced mirror.
 */
export const wordleListResponse = z.object({
    games: z.array(wordleGameRow),
});
/* ---------------- read: word meaning ---------------- */
/**
 * `GET /api/v1/wordle/meaning`. Best-effort, like weather: `definitions` is
 * simply empty when nl.wiktionary.org has no Dutch entry for the word, never an
 * error the caller has to handle. `word` echoes back the query in uppercase so
 * the client can discard a stale in-flight answer. `form_of` is set when the
 * word is an inflected form ("meervoud van arm") and `definitions` are then the
 * base word's.
 */
export const wordMeaningResponse = z.object({
    word: z.string(),
    definitions: z.array(z.string()).max(6),
    form_of: z.string().nullable(),
    source_url: z.string().url().nullable(),
});
//# sourceMappingURL=schemas.js.map